import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';
import { RefractionService } from '../../../../api/refraction.service';
import { enqueueSnackbar } from '../../../../components/snackbar';
import FormProvider from '../../../../components/hook-form/FormProvider';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Scrollbar from '../../../../components/scrollbar';
import { LensMeter } from './LensMeter/LensMeter';
import { AutoRefractor } from './AutoRefractor/AutoRefractor';
import { Subjective } from './Subjective/Subjective';
import { Summary } from './Summary';
import { Final } from './Final/Final';
import { IRefractionSchema } from '../../../../@types/refraction';
import { LoadingButton } from '@mui/lab';
import {
  IAutoRefractorSchema,
  IVisionSchema,
} from '../../../../@types/specsHistory';
import { PreviousRecords } from '../Examination/PreviousRecords';
import { useAppContext } from '../../../../providers/AppContext';
import { DeviceReadingBanner } from '../../../../components/hardware/DeviceReadingBanner';
import { DeviceReading } from '../../../../hooks/useDeviceEvents';
import { getApiErrorMessage } from '../../../../utils/util';

type Props = {
  selectedRefraction: IRefractionSchema;
  tab: any;
  clinicalRecordId: string;
  getAllRefraction: (refractionId: number, clinicalId: number) => void;
  onUpdate?: () => Promise<any> | void;
  visionData: {
    nearVision: IVisionSchema;
    distanceVision: IVisionSchema;
  };
  autoRefractorData: IAutoRefractorSchema;
  setRefraction: (data: IRefractionSchema) => void;
};
const REFRACTION_TABS = [
  { id: 'lens_meter', label: 'Lens Meter', align: 'left' },
  { id: 'auto_refractor', label: 'Auto Refractor', align: 'left' },
  { id: 'subjective', label: 'Subjective', align: 'left' },
  { id: 'final', label: 'Final', align: 'left' },
  { id: 'summary', label: 'Summary', align: 'left' },
];

export default function Refraction({
  selectedRefraction,
  tab,
  clinicalRecordId,
  getAllRefraction,
  onUpdate,
  visionData,
  autoRefractorData,
  setRefraction,
}: Props) {
  const lock = useClinicalRecordLock();

  const [openPreviousRecModal, setOpenPreviousRecModal] = useState(false);

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();
  const [childValue, setChildValue] = useState('lens_meter');

  const showRefractionTabs = () => {
    switch (childValue) {
      case REFRACTION_TABS[0].id:
        return <LensMeter tab={(tab: string) => setChildValue(tab)} />;
      case REFRACTION_TABS[1].id:
        return <AutoRefractor tab={(tab: string) => setChildValue(tab)} />;
      case REFRACTION_TABS[2].id:
        return <Subjective tab={(tab: string) => setChildValue(tab)} />;
      case REFRACTION_TABS[3].id:
        return <Final tab={(tab: string) => setChildValue(tab)} />;
      case REFRACTION_TABS[4].id:
        return <Summary />;
      default:
        break;
    }
  };

  const handleChildChange = (event: React.SyntheticEvent, newValue: string) => {
    setChildValue(newValue);
  };

  const methods = useForm<IRefractionSchema>({
    defaultValues: {
      autoRefractor: {
        distanceRightSPH: autoRefractorData?.rightDistancePD || '',
      },
    },
  });

  const {
    handleSubmit,
    watch,
    formState: { isSubmitting, isLoading },
  } = methods;

  const onSubmit = async (data: IRefractionSchema) => {
    // Amending a signed record: stage the change instead of saving it.
    if (lock.stageIfAmending('refraction', { ...data, clinicalRecordId })) {
      tab('examination');
      return;
    }
    try {
      const refractionService = new RefractionService();
      const response = await refractionService.createRefraction(patientId, {
        ...data,
        clinicalRecordId,
      });

      enqueueSnackbar('Refraction Created Successfully!', {
        variant: 'success',
      });

      const refractionId = response.data.id;
      const clinicalId = response.data.clinicalRecordId;
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
      getAllRefraction(refractionId, clinicalId);

      tab('examination');
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error), {
        variant: 'error',
      });
    }
  };

  const setRefractionData = (data: IRefractionSchema) => {
    if (!data) return;

    const { lensMeter, autoRefractor, subjective, final } = data;

    methods.reset({
      lensMeter: {
        distanceRightSPH:
          lensMeter?.distanceRightSPH ||
          visionData?.distanceVision?.rightSphericalPower ||
          '',
        distanceRightCYL:
          lensMeter?.distanceRightCYL ||
          visionData?.distanceVision?.rightCylinder ||
          '',
        distanceRightAxis: lensMeter?.distanceRightAxis
          ? parseFloat(lensMeter.distanceRightAxis).toFixed(2)
          : visionData?.distanceVision?.rightAxis
            ? parseFloat(visionData.distanceVision.rightAxis).toFixed(2)
            : '',
        distanceRightAidedVA: lensMeter?.distanceRightAidedVA || '',
        distanceRightPrism: lensMeter?.distanceRightPrism || '',
        distanceRightDistancePD: lensMeter?.distanceRightDistancePD
          ? parseFloat(lensMeter?.distanceRightDistancePD).toFixed(2)
          : visionData?.distanceVision?.rightDistancePD
            ? parseFloat(visionData?.distanceVision?.rightDistancePD).toFixed(2)
            : '',
        distanceBothAidedVA: lensMeter?.distanceBothAidedVA || '',
        distanceBothPrism: lensMeter?.distanceBothPrism || '',
        distanceLeftSPH:
          lensMeter?.distanceLeftSPH ||
          visionData?.distanceVision?.leftSphericalPower ||
          '',
        distanceLeftCYL:
          lensMeter?.distanceLeftCYL ||
          visionData?.distanceVision?.leftCylinder ||
          '',
        distanceLeftAxis: lensMeter?.distanceLeftAxis
          ? parseFloat(lensMeter?.distanceLeftAxis).toFixed(2)
          : visionData?.distanceVision?.leftAxis
            ? parseFloat(visionData?.distanceVision?.leftAxis).toFixed(2)
            : '',
        distanceLeftAidedVA: lensMeter?.distanceLeftAidedVA || '',
        distanceLeftPrism: lensMeter?.distanceLeftPrism || '',
        distanceLeftDistancePD: lensMeter?.distanceLeftDistancePD
          ? parseFloat(lensMeter?.distanceLeftDistancePD).toFixed(2)
          : visionData?.distanceVision?.leftDistancePD
            ? parseFloat(visionData?.distanceVision?.leftDistancePD).toFixed(2)
            : '',
        nearRightAdd: lensMeter?.nearRightAdd || '',
        nearRightSPH:
          lensMeter?.nearRightSPH ||
          visionData?.nearVision?.rightSphericalPower ||
          '',
        nearRightCYL:
          lensMeter?.nearRightCYL ||
          visionData?.nearVision?.rightCylinder ||
          '',
        nearRightAxis: lensMeter?.nearRightAxis
          ? parseFloat(lensMeter?.nearRightAxis).toFixed(2)
          : visionData?.nearVision?.rightAxis
            ? parseFloat(visionData?.nearVision?.rightAxis).toFixed(2)
            : '',
        nearRightAidedVA: lensMeter?.nearRightAidedVA || '',
        nearRightPrism: lensMeter?.nearRightPrism || '',
        nearRightNearPD: lensMeter?.nearRightNearPD
          ? parseFloat(lensMeter?.nearRightNearPD).toFixed(2)
          : visionData?.nearVision?.rightDistancePD
            ? parseFloat(visionData?.nearVision?.rightDistancePD).toFixed(2)
            : '',
        nearBothAidedVA: lensMeter?.nearBothAidedVA || '',
        nearBothPrism: lensMeter?.nearBothPrism || '',
        nearLeftAdd: lensMeter?.nearLeftAdd || '',
        nearLeftSPH:
          lensMeter?.nearLeftSPH ||
          visionData?.nearVision?.leftSphericalPower ||
          '',
        nearLeftCYL:
          lensMeter?.nearLeftCYL || visionData?.nearVision?.leftCylinder || '',
        nearLeftAxis: lensMeter?.nearLeftAxis
          ? parseFloat(lensMeter?.nearLeftAxis).toFixed(2)
          : visionData?.nearVision?.leftAxis
            ? parseFloat(visionData?.nearVision?.leftAxis).toFixed(2)
            : '',
        nearLeftAidedVA: lensMeter?.nearLeftAidedVA || '',
        nearLeftPrism: lensMeter?.nearLeftPrism || '',
        nearLeftNearPD:
          lensMeter?.nearLeftNearPD ||
          visionData?.nearVision?.leftDistancePD ||
          '',
        intermediateRightAdd: lensMeter?.intermediateRightAdd || '',
        intermediateRightSPH: lensMeter?.intermediateRightSPH || '',
        intermediateRightCYL: lensMeter?.intermediateRightCYL || '',
        intermediateRightAxis: lensMeter?.intermediateRightAxis
          ? parseFloat(lensMeter?.intermediateRightAxis).toFixed(2)
          : '',
        intermediateRightAidedVA: lensMeter?.intermediateRightAidedVA || '',
        intermediateRightPrism: lensMeter?.intermediateRightPrism || '',
        intermediateRightInterPD: lensMeter?.intermediateRightInterPD
          ? parseFloat(lensMeter?.intermediateRightInterPD).toFixed(2)
          : '',
        intermediateBothAidedVA: lensMeter?.intermediateBothAidedVA || '',
        intermediateBothPrism: lensMeter?.intermediateBothPrism || '',
        intermediateLeftAdd: lensMeter?.intermediateLeftAdd || '',
        intermediateLeftSPH: lensMeter?.intermediateLeftSPH || '',
        intermediateLeftCYL: lensMeter?.intermediateLeftCYL || '',
        intermediateLeftAxis: lensMeter?.intermediateLeftAxis
          ? parseFloat(lensMeter?.intermediateLeftAxis).toFixed(2)
          : '',
        intermediateLeftAidedVA: lensMeter?.intermediateLeftAidedVA || '',
        intermediateLeftPrism: lensMeter?.intermediateLeftPrism || '',
        intermediateLeftInterPD: lensMeter?.intermediateLeftInterPD
          ? parseFloat(lensMeter?.intermediateLeftInterPD).toFixed(2)
          : '',
      },
      autoRefractor: {
        distanceRightSPH:
          autoRefractor?.distanceRightSPH ||
          autoRefractorData?.rightSphericalPower ||
          '',
        distanceRightCYL:
          autoRefractor?.distanceRightCYL ||
          autoRefractorData?.rightCylinder ||
          '',
        distanceRightAxis: autoRefractor?.distanceRightAxis
          ? parseFloat(autoRefractor?.distanceRightAxis).toFixed(2)
          : autoRefractorData?.rightAxis
            ? parseFloat(autoRefractorData?.rightAxis).toFixed(2)
            : '',
        distanceRightAidedVA: autoRefractor?.distanceRightAidedVA || '',
        distanceRightPrism: autoRefractor?.distanceRightPrism || '',
        distanceRightDistancePD: autoRefractor?.distanceRightDistancePD
          ? parseFloat(autoRefractor?.distanceRightDistancePD).toFixed(2)
          : autoRefractorData?.rightDistancePD
            ? parseFloat(autoRefractorData?.rightDistancePD).toFixed(2)
            : '',
        distanceBothAidedVA: autoRefractor?.distanceBothAidedVA || '',
        distanceBothPrism: autoRefractor?.distanceBothPrism || '',
        distanceLeftSPH:
          autoRefractor?.distanceLeftSPH ||
          autoRefractorData?.leftSphericalPower ||
          '',
        distanceLeftCYL:
          autoRefractor?.distanceLeftCYL ||
          autoRefractorData?.leftCylinder ||
          '',
        distanceLeftAxis: autoRefractor?.distanceLeftAxis
          ? parseFloat(autoRefractor?.distanceLeftAxis).toFixed(2)
          : autoRefractorData?.leftAxis
            ? parseFloat(autoRefractorData?.leftAxis).toFixed(2)
            : '',
        distanceLeftAidedVA: autoRefractor?.distanceLeftAidedVA || '',
        distanceLeftPrism: autoRefractor?.distanceLeftPrism || '',
        distanceLeftDistancePD: autoRefractor?.distanceLeftDistancePD
          ? parseFloat(autoRefractor?.distanceLeftDistancePD).toFixed(2)
          : autoRefractorData?.leftDistancePD
            ? parseFloat(autoRefractorData?.leftDistancePD).toFixed(2)
            : '',
        nearRightAdd: autoRefractor?.nearRightAdd || '',
        nearRightSPH: autoRefractor?.nearRightSPH || '',
        nearRightCYL: autoRefractor?.nearRightCYL || '',
        nearRightAxis: autoRefractor?.nearRightAxis
          ? parseFloat(autoRefractor?.nearRightAxis).toFixed(2)
          : '',
        nearRightAidedVA: autoRefractor?.nearRightAidedVA || '',
        nearRightPrism: autoRefractor?.nearRightPrism || '',
        nearRightNearPD: autoRefractor?.nearRightNearPD
          ? parseFloat(autoRefractor?.nearRightNearPD).toFixed(2)
          : '',
        nearBothAidedVA: autoRefractor?.nearBothAidedVA || '',
        nearBothPrism: autoRefractor?.nearBothPrism || '',
        nearLeftAdd: autoRefractor?.nearLeftAdd || '',
        nearLeftSPH: autoRefractor?.nearLeftSPH || '',
        nearLeftCYL: autoRefractor?.nearLeftCYL || '',
        nearLeftAxis: autoRefractor?.nearLeftAxis
          ? parseFloat(autoRefractor?.nearLeftAxis).toFixed(2)
          : '',
        nearLeftAidedVA: autoRefractor?.nearLeftAidedVA || '',
        nearLeftPrism: autoRefractor?.nearLeftPrism || '',
        nearLeftNearPD: autoRefractor?.nearLeftNearPD
          ? parseFloat(autoRefractor?.nearLeftNearPD).toFixed(2)
          : '',
        intermediateRightAdd: autoRefractor?.intermediateRightAdd || '',
        intermediateRightSPH: autoRefractor?.intermediateRightSPH || '',
        intermediateRightCYL: autoRefractor?.intermediateRightCYL || '',
        intermediateRightAxis: autoRefractor?.intermediateRightAxis
          ? parseFloat(autoRefractor?.intermediateRightAxis).toFixed(2)
          : '',
        intermediateRightAidedVA: autoRefractor?.intermediateRightAidedVA || '',
        intermediateRightPrism: autoRefractor?.intermediateRightPrism || '',
        intermediateRightInterPD: autoRefractor?.intermediateRightInterPD
          ? parseFloat(autoRefractor?.intermediateRightInterPD).toFixed(2)
          : '',
        intermediateBothAidedVA: autoRefractor?.intermediateBothAidedVA || '',
        intermediateBothPrism: autoRefractor?.intermediateBothPrism || '',
        intermediateLeftAdd: autoRefractor?.intermediateLeftAdd || '',
        intermediateLeftSPH: autoRefractor?.intermediateLeftSPH || '',
        intermediateLeftCYL: autoRefractor?.intermediateLeftCYL || '',
        intermediateLeftAxis: autoRefractor?.intermediateLeftAxis
          ? parseFloat(autoRefractor?.intermediateLeftAxis).toFixed(2)
          : '',
        intermediateLeftAidedVA: autoRefractor?.intermediateLeftAidedVA || '',
        intermediateLeftPrism: autoRefractor?.intermediateLeftPrism || '',
        intermediateLeftInterPD: autoRefractor?.intermediateLeftInterPD
          ? parseFloat(autoRefractor?.intermediateLeftInterPD).toFixed(2)
          : '',
      },
      subjective: {
        distanceRightSPH: subjective?.distanceRightSPH || '',
        distanceRightCYL: subjective?.distanceRightCYL || '',
        distanceRightAxis: subjective?.distanceRightAxis || '',
        distanceRightAidedVA: subjective?.distanceRightAidedVA || '',
        distanceRightPrism: subjective?.distanceRightPrism || '',
        distanceRightDistancePD: subjective?.distanceRightDistancePD || '',
        distanceBothAidedVA: subjective?.distanceBothAidedVA || '',
        distanceBothPrism: subjective?.distanceBothPrism || '',
        distanceLeftSPH: subjective?.distanceLeftSPH || '',
        distanceLeftCYL: subjective?.distanceLeftCYL || '',
        distanceLeftAxis: subjective?.distanceLeftAxis || '',
        distanceLeftAidedVA: subjective?.distanceLeftAidedVA || '',
        distanceLeftPrism: subjective?.distanceLeftPrism || '',
        distanceLeftDistancePD: subjective?.distanceLeftDistancePD || '',
        nearRightAdd: subjective?.nearRightAdd || '',
        nearRightSPH: subjective?.nearRightSPH || '',
        nearRightCYL: subjective?.nearRightCYL || '',
        nearRightAxis: subjective?.nearRightAxis || '',
        nearRightAidedVA: subjective?.nearRightAidedVA || '',
        nearRightPrism: subjective?.nearRightPrism || '',
        nearRightNearPD: subjective?.nearRightNearPD || '',
        nearBothAidedVA: subjective?.nearBothAidedVA || '',
        nearBothPrism: subjective?.nearBothPrism || '',
        nearLeftAdd: subjective?.nearLeftAdd || '',
        nearLeftSPH: subjective?.nearLeftSPH || '',
        nearLeftCYL: subjective?.nearLeftCYL || '',
        nearLeftAxis: subjective?.nearLeftAxis || '',
        nearLeftAidedVA: subjective?.nearLeftAidedVA || '',
        nearLeftPrism: subjective?.nearLeftPrism || '',
        nearLeftNearPD: subjective?.nearLeftNearPD || '',
        intermediateRightAdd: subjective?.intermediateRightAdd || '',
        intermediateRightSPH: subjective?.intermediateRightSPH || '',
        intermediateRightCYL: subjective?.intermediateRightCYL || '',
        intermediateRightAxis: subjective?.intermediateRightAxis || '',
        intermediateRightAidedVA: subjective?.intermediateRightAidedVA || '',
        intermediateRightPrism: subjective?.intermediateRightPrism || '',
        intermediateRightInterPD: subjective?.intermediateRightInterPD || '',
        intermediateBothAidedVA: subjective?.intermediateBothAidedVA || '',
        intermediateBothPrism: subjective?.intermediateBothPrism || '',
        intermediateLeftAdd: subjective?.intermediateLeftAdd || '',
        intermediateLeftSPH: subjective?.intermediateLeftSPH || '',
        intermediateLeftCYL: subjective?.intermediateLeftCYL || '',
        intermediateLeftAxis: subjective?.intermediateLeftAxis || '',
        intermediateLeftAidedVA: subjective?.intermediateLeftAidedVA || '',
        intermediateLeftPrism: subjective?.intermediateLeftPrism || '',
        intermediateLeftInterPD: subjective?.intermediateLeftInterPD || '',
      },
      final: {
        distanceRightSPH: final?.distanceRightSPH || '',
        distanceRightCYL: final?.distanceRightCYL || '',
        distanceRightAxis: final?.distanceRightAxis || '',
        distanceRightAidedVA: final?.distanceRightAidedVA || '',
        distanceRightPrism: final?.distanceRightPrism || '',
        distanceRightDistancePD: final?.distanceRightDistancePD || '',
        distanceBothAidedVA: final?.distanceBothAidedVA || '',
        distanceBothPrism: final?.distanceBothPrism || '',
        distanceLeftSPH: final?.distanceLeftSPH || '',
        distanceLeftCYL: final?.distanceLeftCYL || '',
        distanceLeftAxis: final?.distanceLeftAxis || '',
        distanceLeftAidedVA: final?.distanceLeftAidedVA || '',
        distanceLeftPrism: final?.distanceLeftPrism || '',
        distanceLeftDistancePD: final?.distanceLeftDistancePD || '',
        nearRightAdd: final?.nearRightAdd || '',
        nearRightSPH: final?.nearRightSPH || '',
        nearRightCYL: final?.nearRightCYL || '',
        nearRightAxis: final?.nearRightAxis || '',
        nearRightAidedVA: final?.nearRightAidedVA || '',
        nearRightPrism: final?.nearRightPrism || '',
        nearRightNearPD: final?.nearRightNearPD || '',
        nearBothAidedVA: final?.nearBothAidedVA || '',
        nearBothPrism: final?.nearBothPrism || '',
        nearLeftAdd: final?.nearLeftAdd || '',
        nearLeftSPH: final?.nearLeftSPH || '',
        nearLeftCYL: final?.nearLeftCYL || '',
        nearLeftAxis: final?.nearLeftAxis || '',
        nearLeftAidedVA: final?.nearLeftAidedVA || '',
        nearLeftPrism: final?.nearLeftPrism || '',
        nearLeftNearPD: final?.nearLeftNearPD || '',
        intermediateRightAdd: final?.intermediateRightAdd || '',
        intermediateRightSPH: final?.intermediateRightSPH || '',
        intermediateRightCYL: final?.intermediateRightCYL || '',
        intermediateRightAxis: final?.intermediateRightAxis || '',
        intermediateRightAidedVA: final?.intermediateRightAidedVA || '',
        intermediateRightPrism: final?.intermediateRightPrism || '',
        intermediateRightInterPD: final?.intermediateRightInterPD || '',
        intermediateBothAidedVA: final?.intermediateBothAidedVA || '',
        intermediateBothPrism: final?.intermediateBothPrism || '',
        intermediateLeftAdd: final?.intermediateLeftAdd || '',
        intermediateLeftSPH: final?.intermediateLeftSPH || '',
        intermediateLeftCYL: final?.intermediateLeftCYL || '',
        intermediateLeftAxis: final?.intermediateLeftAxis || '',
        intermediateLeftAidedVA: final?.intermediateLeftAidedVA || '',
        intermediateLeftPrism: final?.intermediateLeftPrism || '',
        intermediateLeftInterPD: final?.intermediateLeftInterPD || '',
      },
    });
  };

  useEffect(() => {
    if (selectedRefraction) {
      setRefractionData(selectedRefraction);
    } else {
      methods.reset({
        lensMeter: {
          distanceRightSPH: '',
          distanceRightCYL: '',
          distanceRightAxis: '',
          distanceRightAidedVA: '',
          distanceRightPrism: '',
          distanceRightDistancePD: '',
          distanceBothAidedVA: '',
          distanceBothPrism: '',
          distanceLeftSPH: '',
          distanceLeftCYL: '',
          distanceLeftAxis: '',
          distanceLeftAidedVA: '',
          distanceLeftPrism: '',
          distanceLeftDistancePD: '',
          nearRightAdd: '',
          nearRightSPH: '',
          nearRightCYL: '',
          nearRightAxis: '',
          nearRightAidedVA: '',
          nearRightPrism: '',
          nearRightNearPD: '',
          nearBothAidedVA: '',
          nearBothPrism: '',
          nearLeftAdd: '',
          nearLeftSPH: '',
          nearLeftCYL: '',
          nearLeftAxis: '',
          nearLeftAidedVA: '',
          nearLeftPrism: '',
          nearLeftNearPD: '',
          intermediateRightAdd: '',
          intermediateRightSPH: '',
          intermediateRightCYL: '',
          intermediateRightAxis: '',
          intermediateRightAidedVA: '',
          intermediateRightPrism: '',
          intermediateRightInterPD: '',
          intermediateBothAidedVA: '',
          intermediateBothPrism: '',
          intermediateLeftAdd: '',
          intermediateLeftSPH: '',
          intermediateLeftCYL: '',
          intermediateLeftAxis: '',
          intermediateLeftAidedVA: '',
          intermediateLeftPrism: '',
          intermediateLeftInterPD: '',
        },
        autoRefractor: {
          distanceRightSPH: '',
          distanceRightCYL: '',
          distanceRightAxis: '',
          distanceRightAidedVA: '',
          distanceRightPrism: '',
          distanceRightDistancePD: '',
          distanceBothAidedVA: '',
          distanceBothPrism: '',
          distanceLeftSPH: '',
          distanceLeftCYL: '',
          distanceLeftAxis: '',
          distanceLeftAidedVA: '',
          distanceLeftPrism: '',
          distanceLeftDistancePD: '',
          nearRightAdd: '',
          nearRightSPH: '',
          nearRightCYL: '',
          nearRightAxis: '',
          nearRightAidedVA: '',
          nearRightPrism: '',
          nearRightNearPD: '',
          nearBothAidedVA: '',
          nearBothPrism: '',
          nearLeftAdd: '',
          nearLeftSPH: '',
          nearLeftCYL: '',
          nearLeftAxis: '',
          nearLeftAidedVA: '',
          nearLeftPrism: '',
          nearLeftNearPD: '',
          intermediateRightAdd: '',
          intermediateRightSPH: '',
          intermediateRightCYL: '',
          intermediateRightAxis: '',
          intermediateRightAidedVA: '',
          intermediateRightPrism: '',
          intermediateRightInterPD: '',
          intermediateBothAidedVA: '',
          intermediateBothPrism: '',
          intermediateLeftAdd: '',
          intermediateLeftSPH: '',
          intermediateLeftCYL: '',
          intermediateLeftAxis: '',
          intermediateLeftAidedVA: '',
          intermediateLeftPrism: '',
          intermediateLeftInterPD: '',
        },
        subjective: {
          distanceRightSPH: '',
          distanceRightCYL: '',
          distanceRightAxis: '',
          distanceRightAidedVA: '',
          distanceRightPrism: '',
          distanceRightDistancePD: '',
          distanceBothAidedVA: '',
          distanceBothPrism: '',
          distanceLeftSPH: '',
          distanceLeftCYL: '',
          distanceLeftAxis: '',
          distanceLeftAidedVA: '',
          distanceLeftPrism: '',
          distanceLeftDistancePD: '',
          nearRightAdd: '',
          nearRightSPH: '',
          nearRightCYL: '',
          nearRightAxis: '',
          nearRightAidedVA: '',
          nearRightPrism: '',
          nearRightNearPD: '',
          nearBothAidedVA: '',
          nearBothPrism: '',
          nearLeftAdd: '',
          nearLeftSPH: '',
          nearLeftCYL: '',
          nearLeftAxis: '',
          nearLeftAidedVA: '',
          nearLeftPrism: '',
          nearLeftNearPD: '',
          intermediateRightAdd: '',
          intermediateRightSPH: '',
          intermediateRightCYL: '',
          intermediateRightAxis: '',
          intermediateRightAidedVA: '',
          intermediateRightPrism: '',
          intermediateRightInterPD: '',
          intermediateBothAidedVA: '',
          intermediateBothPrism: '',
          intermediateLeftAdd: '',
          intermediateLeftSPH: '',
          intermediateLeftCYL: '',
          intermediateLeftAxis: '',
          intermediateLeftAidedVA: '',
          intermediateLeftPrism: '',
          intermediateLeftInterPD: '',
        },
        final: {
          distanceRightSPH: '',
          distanceRightCYL: '',
          distanceRightAxis: '',
          distanceRightAidedVA: '',
          distanceRightPrism: '',
          distanceRightDistancePD: '',
          distanceBothAidedVA: '',
          distanceBothPrism: '',
          distanceLeftSPH: '',
          distanceLeftCYL: '',
          distanceLeftAxis: '',
          distanceLeftAidedVA: '',
          distanceLeftPrism: '',
          distanceLeftDistancePD: '',
          nearRightAdd: '',
          nearRightSPH: '',
          nearRightCYL: '',
          nearRightAxis: '',
          nearRightAidedVA: '',
          nearRightPrism: '',
          nearRightNearPD: '',
          nearBothAidedVA: '',
          nearBothPrism: '',
          nearLeftAdd: '',
          nearLeftSPH: '',
          nearLeftCYL: '',
          nearLeftAxis: '',
          nearLeftAidedVA: '',
          nearLeftPrism: '',
          nearLeftNearPD: '',
          intermediateRightAdd: '',
          intermediateRightSPH: '',
          intermediateRightCYL: '',
          intermediateRightAxis: '',
          intermediateRightAidedVA: '',
          intermediateRightPrism: '',
          intermediateRightInterPD: '',
          intermediateBothAidedVA: '',
          intermediateBothPrism: '',
          intermediateLeftAdd: '',
          intermediateLeftSPH: '',
          intermediateLeftCYL: '',
          intermediateLeftAxis: '',
          intermediateLeftAidedVA: '',
          intermediateLeftPrism: '',
          intermediateLeftInterPD: '',
        },
      });
    }
  }, [selectedRefraction, autoRefractorData, visionData]);

  const handleDeviceAccepted = (reading: DeviceReading) => {
    const { setValue } = methods;
    if (reading.deviceType === 'LENSMETER') {
      const d = reading.data;
      setValue('lensMeter.distanceRightSPH', String(d.right?.sphere ?? ''));
      setValue('lensMeter.distanceRightCYL', String(d.right?.cylinder ?? ''));
      setValue('lensMeter.distanceRightAxis', String(d.right?.axis ?? ''));
      setValue('lensMeter.distanceRightDistancePD', String(d.right?.pd ?? ''));
      setValue('lensMeter.distanceLeftSPH', String(d.left?.sphere ?? ''));
      setValue('lensMeter.distanceLeftCYL', String(d.left?.cylinder ?? ''));
      setValue('lensMeter.distanceLeftAxis', String(d.left?.axis ?? ''));
      setValue('lensMeter.distanceLeftDistancePD', String(d.left?.pd ?? ''));
      setValue('lensMeter.nearRightAdd', String(d.right?.add ?? ''));
      setValue('lensMeter.nearLeftAdd', String(d.left?.add ?? ''));
      setChildValue('lens_meter');
    } else if (reading.deviceType === 'TONOREF') {
      const ar = reading.data.autoRef;
      setValue(
        'autoRefractor.distanceRightSPH',
        String(ar?.right?.sphere ?? ''),
      );
      setValue(
        'autoRefractor.distanceRightCYL',
        String(ar?.right?.cylinder ?? ''),
      );
      setValue(
        'autoRefractor.distanceRightAxis',
        String(ar?.right?.axis ?? ''),
      );
      setValue(
        'autoRefractor.distanceRightDistancePD',
        String(ar?.right?.pd ?? ''),
      );
      setValue('autoRefractor.distanceLeftSPH', String(ar?.left?.sphere ?? ''));
      setValue(
        'autoRefractor.distanceLeftCYL',
        String(ar?.left?.cylinder ?? ''),
      );
      setValue('autoRefractor.distanceLeftAxis', String(ar?.left?.axis ?? ''));
      setValue(
        'autoRefractor.distanceLeftDistancePD',
        String(ar?.left?.pd ?? ''),
      );
      setChildValue('auto_refractor');
    } else if (reading.deviceType === 'PHOROPTER') {
      const d = reading.data;
      setValue('subjective.distanceRightSPH', String(d.right?.sphere ?? ''));
      setValue('subjective.distanceRightCYL', String(d.right?.cylinder ?? ''));
      setValue('subjective.distanceRightAxis', String(d.right?.axis ?? ''));
      setValue('subjective.distanceRightAidedVA', String(d.right?.va ?? ''));
      setValue('subjective.distanceLeftSPH', String(d.left?.sphere ?? ''));
      setValue('subjective.distanceLeftCYL', String(d.left?.cylinder ?? ''));
      setValue('subjective.distanceLeftAxis', String(d.left?.axis ?? ''));
      setValue('subjective.distanceLeftAidedVA', String(d.left?.va ?? ''));
      setValue('subjective.nearRightAdd', String(d.right?.add ?? ''));
      setValue('subjective.nearRightAidedVA', String(d.right?.nearVa ?? ''));
      setValue('subjective.nearLeftAdd', String(d.left?.add ?? ''));
      setValue('subjective.nearLeftAidedVA', String(d.left?.nearVa ?? ''));
      setChildValue('subjective');
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          width: '100%',
        }}
      >
        <Button
          data-lock-hide
          variant="contained"
          sx={{ marginRight: 7 }}
          onClick={() => setOpenPreviousRecModal(true)}
        >
          Previous Records
        </Button>
      </Box>
      <DeviceReadingBanner
        patientId={patientId}
        onAccepted={handleDeviceAccepted}
      />
      {(!selectedRefraction ||
        Object.keys(selectedRefraction).length === 0) && (
        <Typography sx={{ ml: 1, mt: -4, mb: 2 }} color={'red'}>
          * There's no Refraction added for this clinical record, please edit
          and save!
        </Typography>
      )}
      <Stack
        sx={{
          minHeight: '300px',
        }}
        p={1}
        direction="column"
        mr={2}
      >
        <Scrollbar>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around',
            }}
          >
            <Tabs value={childValue} onChange={handleChildChange}>
              {REFRACTION_TABS.map((head) => (
                <Tab key={head.id} value={head.id} label={head.label} />
              ))}
            </Tabs>
          </Box>
          {showRefractionTabs()}
        </Scrollbar>
      </Stack>
      <Stack direction="row-reverse" mt="40px" gap="30px" mr="17px">
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting || isLoading}
        >
          Save &amp; Next
        </LoadingButton>
      </Stack>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openPreviousRecModal}
        onClose={() => setOpenPreviousRecModal(false)}
      >
        <DialogTitle>Previous Records</DialogTitle>
        <PreviousRecords
          selectedRow={clinicalRecordId}
          setPreviousRecordData={setRefraction}
          tab="refraction"
        />
      </Dialog>
    </FormProvider>
  );
}
